"use client";
import { useEffect, useState, useRef } from "react";
import { animate } from "framer-motion";
import React from "react";
import Link from "next/link";
import ArrowLeft from "@/components/icons/arrow-left";
import Image from "next/image";

function AnimatedNumber({ value, duration = 2 }) {
  const [displayValue, setDisplayValue] = useState("0");

  useEffect(() => {
    const controls = animate(0, value, {
      duration,
      onUpdate(val) {
        setDisplayValue(Math.floor(val).toString()); // No toLocaleString here
      },
    });

    return () => controls.stop();
  }, [value, duration]);

  return (
    <span className="flex space-x-0.5">
      {displayValue.split("").map((char, index) => (
        <span
          key={index}
          className="bg-gradient-to-b from-[#006b87] to-[#054a5c] text-white font-mono text-xl w-5 flex items-center justify-center rounded-sm"
        >
          {char}
        </span>
      ))}
    </span>
  );
}

const Footer = ({ data }) => {
  const [stats, setStats] = useState({ totalVisitors: 0, liveVisitors: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const footerRef = useRef(null);

  useEffect(() => {
    async function fetchVisitorStats() {
      try {
        const res = await fetch("/api/visitor");
        if (!res.ok) {
          console.log(`HTTP error! status: ${res.status}`);
        }
        const data = await res.json();
        setStats(data);
      } catch (err) {
        console.error("Failed to fetch or parse JSON:", err);
      }
    }
    fetchVisitorStats();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      }
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => {
      if (footerRef.current) {
        observer.unobserve(footerRef.current);
      }
    };
  }, []);

  return (
    <footer className="w-full z-20 print:hidden" ref={footerRef}>
      <div className="p-2.5 md:p-5 2xl:p-7 h-fit text-white gradiantGreen md:mb-14 lg:mb-8">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:mx-12 lg:items-center">
          <Image
            src={data.images.logo.src}
            alt={data.images.logo.alt}
            className="hidden lg:block object-cover w-60 lg:w-[27vw] xl:w-[25vw] 2xl:w-[18vw] 2xl:mr-6 h-full bg-white rounded-md py-1 px-2 mb-3 lg:mb-0"
            width={300}
            height={300}
            sizes="(max-width: 640px) 60vw, (max-width: 768px) 60vw, (max-width: 1280px) 27vw, 20vw"
          />
          <div className="bg-white rounded-md flex items-center md:px-5 py-0.5 w-fit px-2 lg:w-96">
            <Image
              src={data.images.contact.src}
              alt={data.images.contact.alt}
              className="object-cover w-9 h-9 lg:w-12 lg:h-12 mr-2"
              width={64}
              height={64}
              unoptimized
            />
            <p className="text-gray-800 font-normal text-sm sm:text-base">
              {data.content.contact}
            </p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row lg:ml-5 lg:mt-5">
          {data.links.map((item, i) => (
            <Link
              key={i}
              href={item.href}
              className="py-3 lg:py-1 px-1.5 md:px-3 flex items-center group"
            >
              <span className="text-white font-medium group-hover:text-lg transition-all duration-300">
                {item.title}
              </span>
              <ArrowLeft className="size-5 ml-2 opacity-0 group-hover:opacity-100 rotate-180 transition-opacity duration-300" />
            </Link>
          ))}
          <div className="lg:text-left lg:w-[30%] ml-1.5 lg:ml-4">
            <h2 className="text-base font-medium mb-2">
              {data.content.address}
            </h2>
            <p className="text-sm font-normal leading-5">
              {data.content.addressData1}
            </p>
            <p className="text-sm font-normal leading-5">
              {data.content.addressData2}
            </p>
          </div>
        </div>
      </div>

      <section className="md:fixed p-1 md:bottom-0 md:left-0 w-full bg-[#013934] text-sm font-normal text-[#DFF0F8] flex flex-col md:flex-row md:justify-start flex-wrap md:items-center z-50">
        <p className="md:p-1.5 px-6 lg:px-0 lg:pl-10 xl:pl-36">
          Copyright © {new Date().getFullYear()} KRECSL All Rights Reserved.{" "}
        </p>
        <div className="hidden md:flex items-center">
          <p className="flex space-x-1 sm:space-x-2 items-center">
            <span className="font-medium text-white ml-5 px-1">
              Total Visitors :
            </span>{" "}
            <AnimatedNumber
              value={stats.totalVisitors}
              duration={3}
              startAnimation={isVisible}
            />{" "}
          </p>
          <p className="flex space-x-1 sm:space-x-2 items-center">
            <span className="font-medium text-white ml-5 md:px-1">
              Live Visitors :
            </span>{" "}
            <AnimatedNumber
              value={stats.liveVisitors}
              duration={3}
              startAnimation={isVisible}
            />
          </p>
        </div>
        <Link
          href="https://www.invensis.net/"
          target="_blank"
          className="lg:hover:underline mx-auto md:ml-auto"
        >
          Powered By Invensis Technologies
        </Link>
      </section>
    </footer>
  );
};

export default React.memo(Footer);
